import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import AppMobileMenu from './AppMobileMenu';
import { Menu } from 'lucide-react';
import Logo from '../common/Logo';
import { navigationColors } from '../../styles/colors';
import { usePaymentDueStatusSync } from '../../hooks/usePaymentDueStatusSync';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../context/I18nContext';
import { getCheckoutState, hasPendingCheckout } from '../../utils/checkoutStorage';
import { consumePostAuthRedirect } from '../../utils/authRedirect';
import { startCheckoutRedirect } from '../../services/stripe';
import CheckoutLoadingScreen from '../auth/CheckoutLoadingScreen';
import DemoTourBar from '../demo/DemoTourBar';
import DemoDeferredMobile from '../demo/DemoDeferredMobile';
import { ensureDemoWorkspace } from '../../services/demoWorkspace';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { APP_MOBILE_LAYOUT_MQ } from '../../utils/mobileContext';
import { isLoggingOutFlagSet } from '../../utils/authReset';

const APP_TITLE = 'Leadsme';

function getAppPageTitle(pathname: string, search: string, t: (key: string) => string): string {
  const path = pathname.replace(/\/$/, '') || '/app';

  if (path === '/app') return t('nav.dashboard');
  if (path.startsWith('/app/calendar')) return t('nav.calendar');
  if (path.startsWith('/app/leads')) return t('nav.leads');
  if (path.startsWith('/app/projects')) return t('nav.projects');
  if (path.startsWith('/app/messages')) return t('nav.messages');
  if (path.startsWith('/app/finances/invoices')) return t('invoices.createTitle');
  if (path.startsWith('/app/finances')) return t('nav.finances');
  if (path.startsWith('/app/organization')) return t('organization.title');
  if (path.startsWith('/app/settings')) {
    const tab = new URLSearchParams(search).get('tab');
    const settingsTabTitles: Record<string, string> = {
      profile: t('settings.profile'),
      preferences: t('settings.preferences'),
      integrations: t('settings.integrations'),
      subscription: t('settings.subscription'),
      account: t('settings.account'),
    };
    if (tab && settingsTabTitles[tab]) return settingsTabTitles[tab];
    return t('nav.settings');
  }

  return APP_TITLE;
}

const Layout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const pendingCheckoutHandledRef = useRef(false);
  const postAuthRedirectHandledRef = useRef(false);

  // Show loading from frame 1 when pending checkout (prevents flash of /app before redirect).
  // Lazy init ensures we never render Layout content when redirecting to Stripe.
  const [isRedirectingToCheckout, setIsRedirectingToCheckout] = useState(() =>
    Boolean(user && hasPendingCheckout())
  );

  // Standard SaaS: Handle pending checkout when user lands on /app after OAuth (e.g. Google).
  // With email/password, handlePostAuthRedirect runs on SignUpPage/LoginPage.
  // With OAuth, user is redirected to /app directly, so we must check here.
  useEffect(() => {
    if (!user || pendingCheckoutHandledRef.current) return;
    if (!hasPendingCheckout()) return;

    pendingCheckoutHandledRef.current = true;
    setIsRedirectingToCheckout(true);
    const { priceId, type } = getCheckoutState();
    if (!priceId || !type) {
      setIsRedirectingToCheckout(false);
      return;
    }

    startCheckoutRedirect(priceId, type).catch((error) => {
      console.error('Error redirecting to checkout from Layout:', error);
      pendingCheckoutHandledRef.current = false;
      setIsRedirectingToCheckout(false);
      navigate('/pricing?checkout=error', { replace: true });
    });
  }, [user, navigate]);

  // Google OAuth always returns to /app — restore deep link (e.g. settings?tab=preferences).
  useEffect(() => {
    if (!user || postAuthRedirectHandledRef.current) return;
    if (hasPendingCheckout()) return;
    if (location.pathname !== '/app') {
      // Already on a deep link; drop any leftover stash.
      if (location.pathname.startsWith('/app/')) {
        postAuthRedirectHandledRef.current = true;
        consumePostAuthRedirect();
      }
      return;
    }

    const target = consumePostAuthRedirect();
    postAuthRedirectHandledRef.current = true;
    if (!target || target === '/app' || target === '/app/') return;
    navigate(target, { replace: true });
  }, [user, location.pathname, navigate]);

  usePaymentDueStatusSync();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [demoBootstrapped, setDemoBootstrapped] = useState(false);
  const [demoActive, setDemoActive] = useState(false);
  const [demoDeferred, setDemoDeferred] = useState(false);
  const isBurgerLayout = useMediaQuery(APP_MOBILE_LAYOUT_MQ);
  /** Guided tour + nav lock only when the left rail is shown (md+) */
  const demoTourVisible = demoActive && !isBurgerLayout;

  // Activate / defer demo workspace before any page fetches data (scope must be set first).
  useEffect(() => {
    if (!user?.id) {
      setDemoBootstrapped(false);
      setDemoActive(false);
      setDemoDeferred(false);
      return;
    }
    // Logout hard-nav in progress — don't bootstrap / flash demo UI.
    if (isLoggingOutFlagSet()) {
      setDemoBootstrapped(false);
      setDemoActive(false);
      setDemoDeferred(false);
      return;
    }
    let cancelled = false;
    ensureDemoWorkspace()
      .then((state) => {
        if (cancelled) return;
        if (isLoggingOutFlagSet()) return;
        setDemoActive(state?.status === 'active');
        setDemoDeferred(state?.status === 'deferred');
      })
      .catch((err) => {
        console.error('Error bootstrapping demo workspace:', err);
        if (!cancelled) {
          setDemoActive(false);
          setDemoDeferred(false);
        }
      })
      .finally(() => {
        if (!cancelled) {
          if (isLoggingOutFlagSet()) return;
          setDemoBootstrapped(true);
        }
      });
    return () => {
      cancelled = true;
    };
    // Re-run when switching burger ↔ left rail so deferred → active.
  }, [user?.id, isBurgerLayout]);

  // Close mobile menu if demo tour locks navigation
  useEffect(() => {
    if (demoTourVisible) setMobileMenuOpen(false);
  }, [demoTourVisible]);

  // Browser tab title: current page · Leadsme (public pages use SEO component)
  useEffect(() => {
    const page = getAppPageTitle(location.pathname, location.search, t);
    document.title = page === APP_TITLE ? APP_TITLE : `${page} - ${APP_TITLE}`;
  }, [location.pathname, location.search, t]);

  const closeSidebar = () => setSidebarOpen(false);
  const closeMobileMenu = () => setMobileMenuOpen(false);

  const handleMenuClick = () => {
    if (demoTourVisible) return;
    setMobileMenuOpen(!mobileMenuOpen);
  };

  if (isRedirectingToCheckout) {
    return <CheckoutLoadingScreen messageKey="auth.redirectingToPayment" />;
  }

  return (
    <div className="flex h-screen min-w-[320px] overflow-x-hidden" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
      {/* Fixed left sidebar - Desktop only */}
      <Sidebar isOpen={sidebarOpen} onClose={closeSidebar} interactionLocked={demoTourVisible} />

      {/* Main column */}
      <div className="flex flex-col flex-1 min-w-0 w-full overflow-hidden md:ml-20">
        {/* Mobile header — below md; from md the icon rail is enough */}
        <div className={`md:hidden sticky top-0 z-50 border-b ${navigationColors.sidebar.borderHeader} px-4 py-3 flex items-center justify-between ${navigationColors.sidebar.background}`}>
          <button
            onClick={handleMenuClick}
            disabled={demoTourVisible}
            className={`p-2 rounded-md ${navigationColors.inactive.text} focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 transition-colors duration-200 disabled:pointer-events-none`}
            aria-label="Toggle menu"
            aria-disabled={demoTourVisible || undefined}
          >
            <Menu className="h-6 w-6" />
          </button>
          <div className="flex-1 flex justify-center">
            <Logo showText={true} textClassName="text-lg" />
          </div>
          <div className="w-10"></div>
        </div>
        {/* Contenu de la page — min-h-full: pages locked fill viewport; long content grows and main scrolls */}
        <main className="flex-1 min-w-0 flex flex-col min-h-0 overflow-y-auto overflow-x-hidden [scrollbar-gutter:stable]">
          <div className="flex min-h-full w-full max-w-full flex-col py-2 px-3 sm:py-3 lg:px-6 xl:pt-3 xl:pb-3 2xl:pt-4 2xl:pb-4">
            {/* Remount when leaving/entering demo scope so pages never keep demo rows on the personal account */}
            {demoBootstrapped ? (
              <Outlet key={demoActive ? 'demo-scope' : 'personal-scope'} />
            ) : null}
          </div>
        </main>
      </div>

      {/* Mobile Menu - Fullscreen overlay */}
      <AppMobileMenu isOpen={mobileMenuOpen && !demoTourVisible} onClose={closeMobileMenu} />

      {demoBootstrapped && <DemoTourBar active={demoTourVisible} />}
      {demoBootstrapped && demoDeferred && isBurgerLayout && <DemoDeferredMobile />}
    </div>
  );
};

export default Layout;
